// 📁 src/pages/OrdersPage.jsx
import { useEffect, useState } from "react";
import { ClipboardList, FileDown, Loader2 } from "lucide-react";

const SUPPLIERS = [
  "egis", "ibsa", "krka", "medochemie", "sandoz",
  "stada", "teva", "viatris", "zentiva",
  "orion", "glenmark", "+pharma"
];
const MONTHS = [1, 2, 3, 4, 5, 6];

function formatCzDateTime(input) {
  try {
    const d = typeof input === "string" ? new Date(input) : input;
    if (Number.isNaN(d?.getTime())) return "—";
    return d.toLocaleString("cs-CZ").replace(/\. /g, ".");
  } catch {
    return "—";
  }
}

async function openOrderPdf({ dodavatel, mesice }) {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  const res = await fetch("/api/orders/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ dodavatel, mesice }),
  });
  if (!res.ok) {
    let msg = `Chyba ${res.status}`;
    try {
      const err = await res.json();
      if (err?.error) msg = err.error;
    } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) alert("Povol prosím vyskakovací okna pro tuto stránku.");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function OrdersPage() {
  const [dodavatel, setDodavatel] = useState("krka");
  const [mesice, setMesice] = useState(3);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ⚡ metadata přímo z DB
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
        const res = await fetch("/api/orders/meta", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const metaJson = await res.json();
          setLastUpdated(metaJson?.lastBatch || null);
        }
      } catch (err) {
        console.warn("Nepodařilo se načíst metadata objednávek:", err?.message || err);
      }
    })();
  }, []);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      await openOrderPdf({ dodavatel, mesice });
    } catch (e) {
      alert(e.message || "Nepodařilo se vygenerovat PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto", padding: 24 }}>
      {/* HLAVIČKA */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{
          height: 40, width: 40, display: "grid", placeItems: "center",
          borderRadius: 12, background: "#059669", color: "#fff"
        }}>
          <ClipboardList size={20} />
        </div>
        <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.2 }}>Návrh objednávky</h1>
        <span style={{ marginLeft: 16, color: "#6b7280", fontSize: 14 }}>
          {lastUpdated
            ? `Poslední export: ${formatCzDateTime(lastUpdated)}`
            : "Zatím nebyl nahrán žádný export"}
        </span>
      </div>

      {/* KARTA */}
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)"
      }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>Parametry</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Vyber dodavatele a počet měsíců zásoby.
          </div>
        </div>

        <div style={{ padding: 20 }}>
          {/* DVOU SLOUPCOVÝ LAYOUT */}
          <div style={{ display: "grid", gap: 24, gridTemplateColumns: "1fr 360px" }}>
            {/* LEVÝ SLOUPEC – DODAVATEL */}
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 10 }}>
                Dodavatel
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {SUPPLIERS.map(s => {
                  const active = dodavatel === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setDodavatel(s)}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 12,
                        border: `1px solid ${active ? "#059669" : "#e5e7eb"}`,
                        background: active ? "#ecfdf5" : "#fff",
                        cursor: "pointer",
                        textTransform: "capitalize",
                        fontWeight: 500
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* PRAVÝ SLOUPEC – Zásoba na + TLAČÍTKO */}
            <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 14, fontWeight: 600, display: "block", marginBottom: 8 }}>
                  Zásoba na
                </label>
                <select
                  value={String(mesice)}
                  onChange={(e) => setMesice(Number(e.target.value))}
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 10,
                    border: "1px solid #e5e7eb", background: "#fff"
                  }}
                >
                  {MONTHS.map(m => (
                    <option key={m} value={m}>
                      {m} {m === 1 ? "měsíc" : m < 5 ? "měsíce" : "měsíců"}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    background: "#059669", color: "#fff", border: "none", borderRadius: 10,
                    padding: "12px 16px", cursor: "pointer", fontWeight: 600,
                    boxShadow: "0 1px 1px rgba(0,0,0,.05)"
                  }}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                  Vygenerovat PDF
                </button>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: "#e5e7eb", margin: "22px 0" }} />

          <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.55 }}>
            Návrh objednávky je vypočten z pohybů na skladě za posledních 6&nbsp;měsíců.
            Neuvažují se ještě neoznámené změny úhrad, dostupnost na distribuci, sezónní výdeje, LPOD ani skladové
            prostory. Data vychází z posledních nahraných exportů z Lekisu – situace se mezitím mohla změnit.
          </div>
        </div>
      </div>
    </div>
  );
}
